import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { menuCategories } from '@/data/menuData';
import { MessageCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export const MenuPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'hot');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const handleWhatsAppOrder = (productName, price) => {
    const phoneNumber = '919994520043';
    const message = `Hi! I would like to order ${productName} (₹${price}) from Insta Cafe.`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    toast.success(`Opening WhatsApp to order ${productName}`);
  };

  const currentCategory = menuCategories.find(cat => cat.id === selectedCategory);
  const filteredItems = currentCategory?.items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Page Header */}
      <div className="bg-primary text-primary-foreground py-16">
        <div className="section-container text-center">
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-4">Our Menu</h1>
          <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
            Explore our delicious range of teas, snacks, shawarmas, and more
          </p>
        </div>
      </div>

      {/* Menu Content */}
      <div className="section-container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <h2 className="text-xl font-display font-bold mb-4">Categories</h2>
              <div className="space-y-2">
                {menuCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${selectedCategory === category.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-muted text-foreground'
                      }`}
                  >
                    <span className="text-xl">{category.icon}</span>
                    <span className="font-medium">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content - Products */}
          <main className="lg:col-span-3">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12"
                />
              </div>
            </div>

            {/* Category Title */}
            <div className="mb-6 flex items-center gap-3">
              <span className="text-4xl">{currentCategory?.icon}</span>
              <div>
                <h2 className="text-3xl font-display font-bold text-foreground">
                  {currentCategory?.name}
                </h2>
                <p className="text-muted-foreground">
                  {filteredItems.length} items available
                </p>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredItems.map((item, index) => (
                <div key={index} className="cafe-card p-4">
                  {/* Image if available */}
                  {item.image && (
                    <div className="relative h-48 mb-4 overflow-hidden rounded-lg">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg text-foreground flex-1">
                      {item.name}
                    </h3>
                    {item.veg !== undefined && (
                      <div className="ml-2">
                        {item.veg ? (
                          <div className="veg-indicator" />
                        ) : (
                          <div className="non-veg-indicator" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.featured && (
                      <Badge variant="secondary" className="text-xs">Featured</Badge>
                    )}
                    {item.bestseller && (
                      <Badge className="bg-secondary text-foreground text-xs">Bestseller</Badge>
                    )}
                  </div>

                  {/* Price & Order */}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-2xl font-bold text-primary">
                      ₹{item.price}
                    </span>
                    <Button
                      size="sm"
                      className="btn-primary"
                      onClick={() => handleWhatsAppOrder(item.name, item.price)}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Order
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredItems.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No items found</p>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MenuPage;